import React from 'react';
import { ProjectGraphData, Project } from '@/types/project';
import { formatCompactCurrency, formatCompactNumber, formatDate } from '@/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  BarChart3,
  FileText,
  Users
} from 'lucide-react';
import ProjectChart from './ProjectChart';
import FundingRoundDropdown from './FundingRoundDropdown';

interface ProjectAnalyticsProps {
  graphData: ProjectGraphData | null;
  projectId: string;
  project: Project | null;
  loading?: boolean;
}

// Helper function to extract enum value (e.g., "BUY" from "OrderType.BUY")
const extractEnumValue = (value: string): string => {
  if (!value) return value;
  // If it contains a dot, extract the part after the dot
  if (value.includes('.')) {
    return value.split('.').pop() || value;
  }
  return value;
};

// Helper function to format deal type for display
const formatDealType = (dealType: string): string => {
  const value = extractEnumValue(dealType);
  // Convert to readable format (e.g., "LIQUID_TOKEN" -> "Liquid Token", "VESTING" -> "Vesting")
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to format order type for display
const formatOrderType = (orderType: string): string => {
  const value = extractEnumValue(orderType);
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const ProjectAnalytics: React.FC<ProjectAnalyticsProps> = ({ graphData, project, loading = false }) => {
  if (!graphData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">No analytics data available</p>
      </div>
    );
  }

  // Calculate summary statistics
  const totalOrders = graphData.orders?.length || 0;
  const totalFundingRounds = graphData.funding_rounds?.length || 0;

  const totalOrderValue = graphData.orders?.reduce((sum, order) => sum + order.offered_amount, 0) || 0;
  const totalFundingRaised = graphData.funding_rounds?.reduce((sum, round) => sum + (round.amount_raised || 0), 0) || 0;

  const latestPrice = graphData.price_history?.[graphData.price_history.length - 1];
  const firstPrice = graphData.price_history?.[0];

  const priceChange = latestPrice && firstPrice 
    ? ((latestPrice.price_usd - firstPrice.price_usd) / firstPrice.price_usd) * 100
    : null;

  // Build stats array with conditional inclusion based on data availability
  const allStats = [
    {
      label: 'Total Orders',
      value: formatCompactNumber(totalOrders),
      icon: <BarChart3 className="text-gray-900 dark:text-gray-100" size={20} />,
      color: 'text-gray-900 dark:text-gray-100',
      hasData: totalOrders > 0
    },
    {
      label: 'Total Order Value',
      value: formatCompactCurrency(totalOrderValue),
      icon: <DollarSign className="text-gray-900 dark:text-gray-100" size={20} />,
      color: 'text-gray-900 dark:text-gray-100',
      hasData: totalOrderValue > 0
    },
    {
      label: 'Funding Rounds',
      value: formatCompactNumber(totalFundingRounds),
      icon: <TrendingUp className="text-gray-900 dark:text-gray-100" size={20} />,
      color: 'text-gray-900 dark:text-gray-100',
      hasData: totalFundingRounds > 0
    },
    {
      label: 'Total Funding Raised',
      value: formatCompactCurrency(totalFundingRaised),
      icon: <DollarSign className="text-gray-900 dark:text-gray-100" size={20} />,
      color: 'text-gray-900 dark:text-gray-100',
      hasData: totalFundingRaised > 0
    },
    // Price stats - only include if we have price history data
    ...(latestPrice ? [
      {
        label: 'Current Price',
        value: `$${latestPrice.price_usd.toFixed(2)}`,
        icon: <DollarSign className="text-gray-900 dark:text-gray-100" size={20} />,
        color: 'text-gray-900 dark:text-gray-100',
        hasData: true
      },
      {
        label: 'Market Cap',
        value: formatCompactCurrency(latestPrice.market_cap_usd),
        icon: <TrendingUp className="text-gray-900 dark:text-gray-100" size={20} />,
        color: 'text-gray-900 dark:text-gray-100',
        hasData: true
      },
      {
        label: 'FDV',
        value: formatCompactCurrency(latestPrice.spot_fdv_usd),
        icon: <TrendingUp className="text-gray-900 dark:text-gray-100" size={20} />,
        color: 'text-gray-900 dark:text-gray-100',
        hasData: true
      },
      {
        label: 'Price Change',
        value: priceChange !== null ? `${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)}%` : '—',
        icon: priceChange !== null && priceChange > 0 ? (
          <TrendingUp className="text-green-500" size={20} />
        ) : priceChange !== null && priceChange < 0 ? (
          <TrendingDown className="text-red-500" size={20} />
        ) : (
          <DollarSign className="text-gray-900 dark:text-gray-100" size={20} />
        ),
        color: priceChange !== null && priceChange > 0 ? 'text-green-500' : priceChange !== null && priceChange < 0 ? 'text-red-500' : 'text-gray-900 dark:text-gray-100',
        hasData: priceChange !== null && priceChange !== 0
      }
    ] : [])
  ];

  // Filter to only show stats that have data
  const stats = allStats.filter(stat => stat.hasData);

  // Prepare stats for display - 8 cards in 2 rows
  const statsRow1 = [
    {
      label: 'Total Orders',
      value: formatCompactNumber(totalOrders),
      icon: <BarChart3 size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
    },
    {
      label: 'Total Order Value',
      value: formatCompactCurrency(totalOrderValue),
      icon: <DollarSign size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
    },
    {
      label: 'Funding Rounds',
      value: formatCompactNumber(totalFundingRounds),
      icon: <TrendingUp size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
    },
    {
      label: 'Total Funding Raised',
      value: formatCompactCurrency(totalFundingRaised),
      icon: <DollarSign size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
    },
  ];

  const statsRow2 = [
    {
      label: 'Current Price',
      value: latestPrice ? `$${latestPrice.price_usd.toFixed(2)}` : '—',
      icon: <DollarSign size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
    },
    {
      label: 'Market Cap',
      value: latestPrice ? formatCompactCurrency(latestPrice.market_cap_usd) : '—',
      icon: <BarChart3 size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
    },
    {
      label: 'FDV',
      value: latestPrice ? formatCompactCurrency(latestPrice.spot_fdv_usd) : '—',
      icon: <FileText size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
    },
    {
      label: 'Price Change',
      value: priceChange !== null ? `${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)}%` : '—',
      icon: priceChange !== null && priceChange > 0 
        ? <TrendingUp size={16} className="text-[#10b981]" />
        : priceChange !== null && priceChange < 0 
          ? <TrendingDown size={16} className="text-[#ef4444]" />
          : <TrendingUp size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      isNegative: priceChange !== null && priceChange < 0,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Project Valuation Chart */}
      <div>
        <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] mb-4">
          Project Valuation Chart
        </h2>
        <ProjectChart graphData={graphData} loading={loading} />
      </div>

      {/* Analytics Overview */}
      <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.32)] border border-white dark:border-[rgba(255,255,255,0.1)] p-6">
        <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] mb-4">
          Analytics Overview
        </h2>
        
        {loading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white dark:bg-[#121212] p-4">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white dark:bg-[#121212] p-4">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Row 1 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statsRow1.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-[#121212] p-4">
                  <div className="flex items-center gap-[6px] mb-2">
                    {stat.icon}
                    <span className="font-['Plus_Jakarta_Sans'] font-medium text-[12px] leading-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                      {stat.label}
                    </span>
                  </div>
                  <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[28px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
            {/* Row 2 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statsRow2.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-[#121212] p-4">
                  <div className="flex items-center gap-[6px] mb-2">
                    {stat.icon}
                    <span className="font-['Plus_Jakarta_Sans'] font-medium text-[12px] leading-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                      {stat.label}
                    </span>
                  </div>
                  <p className={`font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[28px] ${
                    stat.isNegative ? 'text-[#ef4444]' : 'text-[#2d2923] dark:text-[#fdfdfd]'
                  }`}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Funding History & Recent Orders - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funding History */}
        <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.32)] border border-white dark:border-[rgba(255,255,255,0.1)] p-6">
          <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] mb-4">
            Funding History
          </h2>
          
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-[rgba(255,255,255,0.44)] dark:bg-[rgba(28,28,28,0.44)] p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-10 h-10" />
                      <div>
                        <Skeleton className="h-4 w-28 mb-2" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-5 w-16" />
                  </div>
                </div>
              ))}
            </div>
          ) : project?.rounds && project.rounds.length > 0 ? (
            <div className="space-y-4">
              {project.rounds.map((round, index) => (
                <FundingRoundDropdown key={index} round={round} index={index} />
              ))}
            </div>
          ) : (
            <p className="font-['Plus_Jakarta_Sans'] text-[13px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
              No funding history available
            </p>
          )}
        </div>

        {/* Recent Orders */}
        <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.32)] border border-white dark:border-[rgba(255,255,255,0.1)] p-6">
          <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] mb-4">
            Recent Orders
          </h2>
          
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.08)]">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-12 h-6" />
                    <div>
                      <Skeleton className="h-4 w-16 mb-1" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
          ) : graphData.orders && graphData.orders.length > 0 ? (
            <div className="space-y-0">
              {graphData.orders.slice(0, 10).map((order, index) => {
                const orderType = extractEnumValue(order.type);
                const dealType = extractEnumValue(order.deal_type);
                const isBuy = orderType.includes('BUY') || orderType === 'BUY';
                
                return (
                  <div 
                    key={index} 
                    className="flex items-center justify-between py-3 border-b border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.08)] last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      {/* Order Type Badge */}
                      <div className={`px-2 py-1 text-[11px] font-['Plus_Jakarta_Sans'] font-medium ${
                        isBuy 
                          ? 'bg-[#c8ed03] text-[#2d2923]' 
                          : 'bg-[#f59e0b] text-white'
                      }`}>
                        {isBuy ? 'BUY' : 'SELL'}
                      </div>
                      <div>
                        <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                          {formatDealType(dealType)}
                        </p>
                        <p className="font-['Plus_Jakarta_Sans'] font-normal text-[11px] leading-[14px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">
                          {formatDate(order.date)} • {order.round || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {formatCompactCurrency(order.offered_amount)}
                      </p>
                      <p className="font-['Plus_Jakarta_Sans'] font-normal text-[11px] leading-[14px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">
                        FDV: {formatCompactCurrency(order.offered_fully_diluted_valuation)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="font-['Plus_Jakarta_Sans'] text-[13px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
              No orders available
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectAnalytics; 